import { useState } from "react";
import { toast } from "react-toastify";

import PasswordUpgradeService from "src/Services/API/password-upgrade.service";
import { handleError, PASSWORD_HINT, validatePassword } from "src/Common/function";
import { PrimaryButton } from "src/Pages/WO/Components/upload/uploadModalUI";
import { SettingsCard, TextField } from "./accountUI";

// Changes the signed-in user's password. The current password is required (it
// counts against the sign-in attempt limits on the server), and the new one
// must meet the password rules. This session stays signed in; the user's other
// sessions — other browsers, the mobile app — are signed out, and the app's
// Face ID sign-in has to be turned on again there.
function ChangePasswordSection() {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const changeClicked = async () => {
    if (saving) return;
    const result = validatePassword(newPassword);
    const nextErrors = {
      current: !currentPassword,
      new: !result.success,
      confirm: newPassword !== confirmPassword,
    };
    setErrors(nextErrors);
    if (nextErrors.current) return toast("Please enter your current password");
    if (nextErrors.new) return toast(result.message);
    if (nextErrors.confirm) return toast("Passwords don't match");

    setSaving(true);
    try {
      await PasswordUpgradeService.changePassword(currentPassword, newPassword);
      // A pending "choose a stronger password" request is answered too.
      PasswordUpgradeService.clear();
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setErrors({});
      toast("Password changed. You've been signed out on your other devices.");
    } catch (error) {
      // e.g. a wrong current password, or a new one the server finds too common.
      handleError(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsCard
      title="Password"
      description={`${PASSWORD_HINT} Changing it signs you out on your other devices.`}
      footer={
        <PrimaryButton onClick={changeClicked} loading={saving} loadingLabel="Changing...">
          Change password
        </PrimaryButton>
      }
    >
      <div className="flex flex-col gap-4 max-w-[400px]">
        <TextField
          label="Current password"
          type="password"
          value={currentPassword}
          onChange={setCurrentPassword}
          error={errors.current}
          autoComplete="current-password"
          onEnter={changeClicked}
        />
        <TextField
          label="New password"
          type="password"
          value={newPassword}
          onChange={setNewPassword}
          error={errors.new}
          autoComplete="new-password"
          onEnter={changeClicked}
        />
        <TextField
          label="Confirm new password"
          type="password"
          value={confirmPassword}
          onChange={setConfirmPassword}
          error={errors.confirm}
          autoComplete="new-password"
          onEnter={changeClicked}
        />
      </div>
    </SettingsCard>
  );
}

export default ChangePasswordSection;
